<!--
  @component HeroBlock — the Projects-home centerpiece.
  tier:      home block (kind: "hero")
  purpose:   a personal, landing-page hero — time-aware greeting, the day,
             a one-line account pulse, and the primary "New project" action.
             Background is a layered Slate-Ink gradient today; `config.media`
             is the reserved seam for a user hero image / background video.
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useProvenanceIO } from "../../../../global/io/nodes/deployment/ProvenanceIO.svelte";
  import { useHomeActions } from "../home-context.js";
  import { greeting, longDate, projectRollup, accountStats } from "../home-util.js";

  interface Props { config?: Readonly<Record<string, unknown>>; }
  const { config }: Props = $props();

  const identity = useIdentityIO();
  const provenance = useProvenanceIO();
  const actions = useHomeActions();

  const media = $derived.by(() => {
    const m = config?.["media"];
    return typeof m === "string" ? m : null;
  });
  const name = $derived(
    (identity.user?.displayName ?? identity.user?.email ?? "there").split("@")[0] ?? "there",
  );
  const stats = $derived.by(() => {
    const roll = projectRollup(provenance.events);
    return accountStats(identity.projects, provenance.events, roll);
  });

  const pulse = $derived.by(() => {
    const s = stats;
    if (s.projects === 0) return "Spin up your first project — code, deploy, and ship in minutes.";
    const bits = [`${s.projects} ${s.projects === 1 ? "project" : "projects"}`];
    if (s.live > 0) bits.push(`${s.live} live`);
    if (s.deploys24h > 0) bits.push(`${s.deploys24h} ${s.deploys24h === 1 ? "deploy" : "deploys"} today`);
    return bits.join("  ·  ");
  });
</script>

<section class="hero" class:has-media={!!media}>
  {#if media}
    <div class="media" style:background-image={`url(${media})`}></div>
  {/if}
  <div class="glow glow-a" aria-hidden="true"></div>
  <div class="glow glow-b" aria-hidden="true"></div>

  <div class="inner">
    <p class="eyebrow">{longDate()}</p>
    <h1>{greeting()}, {name}</h1>
    <p class="pulse">{pulse}</p>
    <div class="cta">
      <button type="button" class="primary" onclick={() => actions.openNewProject()}>
        <span class="plus" aria-hidden="true">+</span> New project
      </button>
      {#if stats.issues > 0}
        <span class="attn">{stats.issues} need attention</span>
      {:else if stats.projects > 0}
        <span class="ok"><span class="led"></span>All systems healthy</span>
      {/if}
    </div>
  </div>
</section>

<style>
  .hero {
    position: relative;
    overflow: hidden;
    border: 0.5px solid var(--border);
    border-radius: var(--radius-xl, 18px);
    background:
      radial-gradient(120% 140% at 100% 0%, color-mix(in oklab, var(--accent, #6ea8fe) 10%, transparent) 0%, transparent 55%),
      linear-gradient(160deg, var(--surface-raised) 0%, var(--surface-sunken) 100%);
    box-shadow: var(--shadow-md);
    min-height: 232px;
    display: flex;
  }
  .media {
    position: absolute; inset: 0; background-size: cover; background-position: center;
  }
  .has-media::after {
    content: ""; position: absolute; inset: 0;
    background: linear-gradient(90deg, rgba(10,11,14,0.82) 0%, rgba(10,11,14,0.5) 60%, rgba(10,11,14,0.2) 100%);
  }
  .glow { position: absolute; border-radius: 50%; filter: blur(40px); pointer-events: none; opacity: 0.9; }
  .glow-a { width: 320px; height: 320px; right: -40px; top: -120px; background: var(--accent-glow, rgba(110,168,254,0.18)); }
  .glow-b { width: 260px; height: 260px; right: 200px; bottom: -160px; background: var(--accent-subtle, rgba(110,168,254,0.1)); opacity: 0.6; }

  .inner {
    position: relative; z-index: 1;
    display: flex; flex-direction: column; gap: var(--space-3);
    padding: var(--space-7, 32px) var(--space-8, 40px);
    justify-content: center;
    max-width: 720px;
  }
  .eyebrow {
    margin: 0; font-size: 11px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--text-tertiary);
  }
  h1 {
    margin: 0; font-size: 32px; font-weight: 600; letter-spacing: -0.03em; line-height: 1.1;
    color: var(--text-primary);
  }
  .pulse { margin: 2px 0 0; font-size: 14px; color: var(--text-secondary); }
  .cta { display: flex; align-items: center; gap: var(--space-3); margin-top: var(--space-3); }
  .primary {
    appearance: none; display: inline-flex; align-items: center; gap: 7px;
    background: var(--text-primary); color: var(--surface-base);
    border: 0; border-radius: var(--radius-md); height: 38px; padding: 0 var(--space-4);
    font: inherit; font-size: 13.5px; font-weight: 600; cursor: pointer;
    transition: transform var(--ease-fast), opacity var(--ease-fast);
  }
  .primary:hover { transform: translateY(-1px); opacity: 0.92; }
  .plus { font-size: 17px; font-weight: 500; line-height: 1; }
  .attn, .ok {
    display: inline-flex; align-items: center; gap: 7px;
    font-size: 12px; font-weight: 500; border-radius: var(--radius-pill);
    padding: 5px 11px; border: 0.5px solid var(--border-subtle);
  }
  .attn { color: var(--warning, #e6b450); background: color-mix(in oklab, var(--warning, #e6b450) 12%, transparent); }
  .ok { color: var(--text-secondary); background: var(--bg-hover); }
  .ok .led { width: 7px; height: 7px; border-radius: var(--radius-pill); background: var(--success); box-shadow: 0 0 0 3px color-mix(in oklab, var(--success) 28%, transparent); }
</style>
